import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

const PICKED_BY_TEXT = {
  choice: "your choice",
  name_match: "matches the brand name",
  most_common: "most rows said so",
  brand_name: "no manufacturer in the file",
};

/** "Same as the brand" is sent as the brand's own name, which the API then resolves. */
function sameAsBrandValue(brand) {
  return `__brand__:${brand.name}`;
}

/**
 * One row per new brand: the manufacturer it will sit under, how that was
 * decided, and a dropdown to change it. Brands whose rows disagreed about the
 * manufacturer come first. The dropdown offers the file's candidates with
 * their row counts, "Same as the brand", and the organization's existing
 * manufacturers.
 *
 * @param {object[]} newBrands       `new_brands` from the dry run
 * @param {{id: number, label: string}[]} manufacturerOptions  existing manufacturers
 * @param {boolean} disabled
 * @param {(brandName: string, manufacturerName: string) => void} onChoose
 */
export default function BrandManufacturersPanel({
  newBrands = [],
  manufacturerOptions = [],
  disabled = false,
  onChoose,
}) {
  if (newBrands.length === 0) return null;

  const ordered = [...newBrands].sort((a, b) => {
    const conflictA = (a.candidates?.length ?? 0) > 1 ? 0 : 1;
    const conflictB = (b.candidates?.length ?? 0) > 1 ? 0 : 1;
    return conflictA - conflictB || a.name.localeCompare(b.name);
  });
  const conflicted = ordered.filter((b) => (b.candidates?.length ?? 0) > 1);

  return (
    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
      <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
        Manufacturers for new brands
      </AlertTitle>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Each new brand is created under one manufacturer.
        {conflicted.length > 0 &&
          ` ${conflicted.length} ${
            conflicted.length === 1 ? "brand" : "brands"
          } in the file ${
            conflicted.length === 1 ? "lists" : "list"
          } more than one manufacturer; the pick is shown below and can be changed.`}
      </Typography>

      {ordered.map((brand) => {
        const candidateNames = new Set(
          (brand.candidates ?? []).map((c) => c.name)
        );
        const currentValue = candidateNames.has(brand.manufacturer)
          ? brand.manufacturer
          : brand.manufacturer === brand.name
            ? sameAsBrandValue(brand)
            : brand.manufacturer;

        return (
          <Box
            key={brand.name}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              mb: 1,
            }}
          >
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="body2" fontWeight={600}>
                {brand.name}{" "}
                {(brand.candidates?.length ?? 0) > 1 && (
                  <Chip
                    label="conflict"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                used by {brand.row_count}{" "}
                {brand.row_count === 1 ? "row" : "rows"} ·{" "}
                {PICKED_BY_TEXT[brand.picked_by] ?? brand.picked_by}
                {brand.manufacturer_is_new ? " · manufacturer will be created" : ""}
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              value={currentValue}
              disabled={disabled}
              onChange={(e) => {
                const value = e.target.value;
                onChoose(
                  brand.name,
                  value === sameAsBrandValue(brand) ? brand.name : value
                );
              }}
              sx={{ minWidth: 280 }}
              inputProps={{
                "aria-label": `Manufacturer for new brand ${brand.name}`,
              }}
            >
              {(brand.candidates?.length ?? 0) > 0 && (
                <ListSubheader>Named in the file</ListSubheader>
              )}
              {(brand.candidates ?? []).map((c) => (
                <MenuItem key={`file:${c.name}`} value={c.name}>
                  {c.name} ({c.row_count}{" "}
                  {c.row_count === 1 ? "row" : "rows"})
                </MenuItem>
              ))}
              <ListSubheader>Other</ListSubheader>
              <MenuItem value={sameAsBrandValue(brand)}>
                Same as the brand: {brand.name}
              </MenuItem>
              {manufacturerOptions.length > 0 && (
                <ListSubheader>Your manufacturers</ListSubheader>
              )}
              {manufacturerOptions
                .filter((m) => !candidateNames.has(m.label))
                .map((m) => (
                  <MenuItem key={`existing:${m.id}`} value={m.label}>
                    {m.label}
                  </MenuItem>
                ))}
              {!candidateNames.has(brand.manufacturer) &&
                brand.manufacturer !== brand.name &&
                !manufacturerOptions.some((m) => m.label === brand.manufacturer) && (
                  <MenuItem value={brand.manufacturer}>{brand.manufacturer}</MenuItem>
                )}
            </TextField>
          </Box>
        );
      })}
    </Alert>
  );
}
